import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { CustomerType } from '@kalachand/shared';
import { apiGet, getApiError, type PaginatedResult } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/Card';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';
import { Input } from '../../components/Input';
import { EmptyState } from '../../components/EmptyState';
import { Pagination } from '../../components/Pagination';
import { StatusPill } from '../../components/StatusPill';
import { cn } from '../../lib/cn';

const PAGE_SIZE = 20;

interface Customer {
  id: string;
  customerCode: string;
  type: CustomerType;
  name: string;
  phone: string;
  altPhones?: string[];
  email: string | null;
  companyName: string | null;
  vatNumber: string | null;
  brn: string | null;
  contactPerson: string | null;
  vatExcluded: boolean;
  address: string | null;
  city: string | null;
  preferredChannel: string;
  preferredShop?: { id: string; code: string; name: string } | null;
  updatedAt: string;
}

const TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'individual', label: 'Individuals' },
  { key: 'company', label: 'Companies' },
];

export function CustomersPage(): JSX.Element {
  const { perms } = useAuth();
  // Shop staff see only their shop; cross-shop roles (incl. operations) see all.
  // Mirror the backend gate (customers.view_all) rather than home-shop presence.
  const scoped = !perms.has('customers.view_all');
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'individual' | 'company'>('all');
  const [page, setPage] = useState(1);

  const customers = useQuery({
    queryKey: ['customers', q, typeFilter, page],
    queryFn: () =>
      apiGet<PaginatedResult<Customer>>('/customers', {
        page,
        pageSize: PAGE_SIZE,
        ...(q.length >= 2 ? { q } : {}),
        ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
      }),
  });

  const rows = customers.data?.rows ?? [];
  const total = customers.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = customers.data?.page ?? page;

  return (
    <PageScaffold
      scroll={false}
      title="Customers"
      subtitle={`${scoped ? "Your shop's customers" : 'Customers across every shop'} — lookup by name, phone, code, VAT or BRN.`}
    >
      <Card className="shrink-0" bodyClassName="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <Input
              id="cust-search"
              placeholder="Search by name, phone, company, VAT, BRN, C-xxxxx…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="flex gap-1.5">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => {
                  setTypeFilter(f.key as typeof typeFilter);
                  setPage(1);
                }}
                className={cn(
                  'px-3 py-1.5 rounded text-xs uppercase tracking-wide transition border',
                  typeFilter === f.key
                    ? 'bg-accent text-white border-accent'
                    : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <DataTableCard
        footer={
          total > 0 ? (
            <Pagination
              page={safePage}
              pageCount={pageCount}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              unit="customer"
            />
          ) : undefined
        }
      >
        {customers.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : customers.isError ? (
          <EmptyState
            title="Could not load customers"
            description={getApiError(customers.error).message}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No customers"
            description={
              q ? 'Try a different search.' : 'Customers appear here as you create orders.'
            }
          />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">Code</th>
                <th className="px-6 py-2 font-medium">Customer</th>
                <th className="px-6 py-2 font-medium">Type</th>
                <th className="px-6 py-2 font-medium">Phone</th>
                <th className="px-6 py-2 font-medium">VAT</th>
                <th className="px-6 py-2 font-medium">Shop</th>
                <th className="px-6 py-2 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const displayName = c.type === 'company' ? (c.companyName ?? c.name) : c.name;
                const secondary =
                  c.type === 'company' ? `Contact: ${c.contactPerson ?? c.name}` : c.email;
                return (
                  <tr key={c.id} className="border-b border-rule-2 hover:bg-paper-2">
                    <td className="px-6 py-3 kc-mono text-xs text-ink-3">{c.customerCode}</td>
                    <td className="px-6 py-3">
                      <Link
                        to={`/customers/${c.id}`}
                        className="text-ink hover:text-accent font-medium"
                      >
                        {displayName}
                      </Link>
                      {secondary && (
                        <div className="text-xs text-ink-3 mt-0.5 truncate max-w-xs">
                          {secondary}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <StatusPill tone={c.type === 'company' ? 'accent' : 'neutral'}>
                        {c.type}
                      </StatusPill>
                    </td>
                    <td className="px-6 py-3 kc-mono text-xs">
                      {c.phone}
                      {(c.altPhones?.length ?? 0) > 0 && (
                        <span className="text-ink-4" title={c.altPhones!.join(', ')}>
                          {' '}
                          +{c.altPhones!.length}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {c.vatExcluded ? (
                        <StatusPill tone="info">excluded</StatusPill>
                      ) : c.vatNumber ? (
                        <span className="text-xs kc-mono text-ink-3">{c.vatNumber}</span>
                      ) : (
                        <span className="text-xs text-ink-4">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-xs text-ink-3">
                      {c.preferredShop ? (
                        <span className="kc-mono">{c.preferredShop.code}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-6 py-3 text-xs text-ink-3">
                      {new Date(c.updatedAt).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </DataTableCard>
    </PageScaffold>
  );
}
